import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  seedDefaultProducts,
  getProductWithCustomizations,
  createCustomizationCategory,
  updateCustomizationCategory,
  deleteCustomizationCategory,
  createCustomizationOption,
  updateCustomizationOption,
  deleteCustomizationOption,
  copyCustomizations,
} from '@/lib/api';
import { 
  Plus, Search, Edit2, Trash2, Tag, Settings, Layers, 
  ChevronRight, GripVertical, Link2, Sparkles, Copy
} from 'lucide-react';

const optionTypes = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Multi-line Text' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
];

const fabricSelectorTypes = [
  { value: 'main', label: 'Main Fabric' },
  { value: 'lining', label: 'Lining Fabric' },
  { value: 'contrast', label: 'Contrast Fabric' },
  { value: 'button', label: 'Button Code' },
];

const normalizePrefixKey = (value = '') =>
  String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');

const normalizeGarmentTypeKey = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

// Sortable Category Component
const SortableCategory = ({ category, children, handleEditCategory, handleDeleteCategory, sharedCustomization }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `cat_${category.category_id}` }); // Prefixed ID

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem 
        value={category.category_id}
        className="border-white/5"
      >
        <div className="flex items-center group">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-3 hover:bg-white/10 rounded flex items-center"
            data-testid={`drag-handle-category-${category.category_id}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
          <AccordionTrigger className="px-4 hover:bg-white/5 flex-1 [&>svg]:hidden">
            <div className="flex items-center gap-3 flex-1">
              <Badge variant="outline" className="text-xs font-mono w-8 justify-center">
                {category.display_order}
              </Badge>
              <span className="font-medium">{category.name}</span>
              <Badge variant="outline" className="text-xs">
                {category.options?.length || 0} options
              </Badge>
            </div>
          </AccordionTrigger>
        </div>
        <AccordionContent className="px-4 pb-4">
          {children}
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};

// Sortable Option Component
const SortableOption = ({ option, handleEditOption, handleDeleteOption, sharedCustomization }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `opt_${option.option_id}` }); // Prefixed ID

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      <Badge variant="secondary" className="text-xs font-mono w-8 justify-center">
        {option.display_order}
      </Badge>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{option.name}</span>
          {option.is_required && (
            <Badge variant="destructive" className="text-xs">Required</Badge>
          )}
          {option.show_on_invoice === false && (
            <Badge variant="outline" className="text-xs" data-testid={`option-hidden-on-invoice-${option.option_id}`}>
              Hidden on invoice
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">{option.option_type}</Badge>
        </div>
      </div>
      {!sharedCustomization && (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditOption(option)}
          >
            <Edit2 className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => handleDeleteOption(option.option_id)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCustomizations, setProductCustomizations] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceProductId, setCopySourceProductId] = useState('__none__');
  const [copyReplaceExisting, setCopyReplaceExisting] = useState(false);
  const [copying, setCopying] = useState(false);
  
  const [productForm, setProductForm] = useState({
    name: '',
    base_price: '',
    description: '',
    product_prefix: '',
    default_garment_type: '',
    shared_customization_product_id: '__none__',
  });

  const [categoryForm, setCategoryForm] = useState({
    product_id: '',
    name: '',
    display_order: '1.0',
    description: '',
  });

  const [optionForm, setOptionForm] = useState({
    category_id: '',
    name: '',
    option_type: 'text',
    options: '',
    price_impact: '0',
    default_value: '',
    display_order: '1.0',
    is_required: false,
    show_on_invoice: true,
    is_fabric_selector: false,
    fabric_selector_type: '__none__',
    conditional_on_option_id: '__none__',
    conditional_operator: 'equals',
    conditional_on_values: '',
    placeholder: '',
  });

  const technicalGarmentKeyPreview = normalizeGarmentTypeKey(productForm.default_garment_type || productForm.name);
  const technicalPrefixPreview = normalizePrefixKey(productForm.product_prefix);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchProductCustomizations(selectedProduct.product_id);
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductCustomizations = async (productId) => {
    try {
      const response = await getProductWithCustomizations(productId);
      setProductCustomizations(response.data);
    } catch (error) {
      toast.error('Failed to fetch customizations');
    }
  };

  const handleSeedProducts = async () => {
    try {
      await seedDefaultProducts();
      toast.success('Default products seeded successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to seed products');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        base_price: parseFloat(productForm.base_price),
        shared_customization_product_id: productForm.shared_customization_product_id === '__none__' ? null : (productForm.shared_customization_product_id || null),
      };
      
      if (editingProduct) {
        await updateProduct(editingProduct.product_id, data);
        toast.success('Product updated successfully');
      } else {
        await createProduct(data);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      base_price: product.base_price.toString(),
      description: product.description || '',
      product_prefix: product.product_prefix || '',
      default_garment_type: product.default_garment_type || '',
      shared_customization_product_id: product.shared_customization_product_id || '__none__',
    });
    setDialogOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure? This will delete all customizations too.')) return;
    try {
      await deleteProduct(productId);
      toast.success('Product deleted successfully');
      if (selectedProduct?.product_id === productId) {
        setSelectedProduct(null);
        setProductCustomizations(null);
      }
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      base_price: '',
      description: '',
      product_prefix: '',
      default_garment_type: '',
      shared_customization_product_id: '__none__',
    });
  };

  // Copy customizations handler
  const handleCopyCustomizations = async () => {
    if (!selectedProduct || copySourceProductId === '__none__') return;
    
    setCopying(true);
    try {
      const response = await copyCustomizations(
        selectedProduct.product_id, 
        copySourceProductId, 
        copyReplaceExisting
      );
      toast.success(response.data.message);
      setCopyDialogOpen(false);
      setCopySourceProductId('__none__');
      setCopyReplaceExisting(false);
      fetchProductCustomizations(selectedProduct.product_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy customizations');
    } finally {
      setCopying(false);
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...categoryForm,
        display_order: parseFloat(categoryForm.display_order),
      };
      
      if (editingCategory) {
        await updateCustomizationCategory(editingCategory.category_id, data);
        toast.success('Category updated');
      } else {
        await createCustomizationCategory(data);
        toast.success('Category created');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchProductCustomizations(selectedProduct.product_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      product_id: category.product_id,
      name: category.name,
      display_order: category.display_order.toString(),
      description: category.description || '',
    });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its options?')) return;
    try {
      await deleteCustomizationCategory(categoryId);
      toast.success('Category deleted');
      fetchProductCustomizations(selectedProduct.product_id);
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      product_id: selectedProduct?.product_id || '',
      name: '',
      display_order: '1.0',
      description: '',
    });
  };

  // Option handlers
  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...optionForm,
        price_impact: parseFloat(optionForm.price_impact) || 0,
        display_order: parseFloat(optionForm.display_order),
        options: optionForm.options ? optionForm.options.split(',').map(o => o.trim()).filter(Boolean) : [],
        conditional_on_values: optionForm.conditional_on_values ? optionForm.conditional_on_values.split(',').map(v => v.trim()).filter(Boolean) : [],
        conditional_on_option_id: optionForm.conditional_on_option_id === '__none__' ? null : (optionForm.conditional_on_option_id || null),
        conditional_operator: optionForm.conditional_operator || 'equals',
        fabric_selector_type: optionForm.is_fabric_selector ? (optionForm.fabric_selector_type === '__none__' ? null : optionForm.fabric_selector_type) : null,
      };
      
      if (editingOption) {
        await updateCustomizationOption(editingOption.option_id, data);
        toast.success('Option updated');
      } else {
        await createCustomizationOption(data);
        toast.success('Option created');
      }
      setOptionDialogOpen(false);
      resetOptionForm();
      fetchProductCustomizations(selectedProduct.product_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditOption = (option, categoryId) => {
    setEditingOption(option);
    setSelectedCategoryId(categoryId);
    setOptionForm({
      category_id: option.category_id,
      name: option.name,
      option_type: option.option_type,
      options: option.options?.join(', ') || '',
      price_impact: option.price_impact?.toString() || '0',
      default_value: option.default_value || '',
      display_order: option.display_order?.toString() || '1.0',
      is_required: option.is_required || false,
      show_on_invoice: option.show_on_invoice !== false,
      is_fabric_selector: option.is_fabric_selector || false,
      fabric_selector_type: option.fabric_selector_type || '__none__',
      conditional_on_option_id: option.conditional_on_option_id || '__none__',
      conditional_operator: option.conditional_operator || 'equals',
      conditional_on_values: option.conditional_on_values?.join(', ') || '',
      placeholder: option.placeholder || '',
    });
    setOptionDialogOpen(true);
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;
    try {
      await deleteCustomizationOption(optionId);
      toast.success('Option deleted');
      fetchProductCustomizations(selectedProduct.product_id);
    } catch (error) {
      toast.error('Failed to delete option');
    }
  };

  const resetOptionForm = () => {
    setEditingOption(null);
    setOptionForm({
      category_id: selectedCategoryId || '',
      name: '',
      option_type: 'text',
      options: '',
      price_impact: '0',
      default_value: '',
      display_order: '1.0',
      is_required: false,
      show_on_invoice: true,
      is_fabric_selector: false,
      fabric_selector_type: '__none__',
      conditional_on_option_id: '__none__',
      conditional_operator: 'equals',
      conditional_on_values: '',
      placeholder: '',
    });
  };

  const openAddCategory = () => {
    resetCategoryForm();
    setCategoryForm(prev => ({ ...prev, product_id: selectedProduct.product_id }));
    setCategoryDialogOpen(true);
  };

  const openAddOption = (categoryId) => {
    resetOptionForm();
    setSelectedCategoryId(categoryId);
    setOptionForm(prev => ({ ...prev, category_id: categoryId }));
    setOptionDialogOpen(true);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.product_prefix?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Get all options for conditional logic dropdown
  const getAllOptions = () => {
    if (!productCustomizations?.categories) return [];
    return productCustomizations.categories.flatMap(cat => 
      (cat.options || []).map(opt => ({ ...opt, categoryName: cat.name }))
    );
  };
  // Handle category drag end
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Only handle category drags (IDs prefixed with 'cat_')
    if (!active.id.startsWith('cat_')) return;
    
    const oldIndex = productCustomizations.categories.findIndex(c => `cat_${c.category_id}` === active.id);
    const newIndex = productCustomizations.categories.findIndex(c => `cat_${c.category_id}` === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newCategories = arrayMove(productCustomizations.categories, oldIndex, newIndex);
    
    // Update display_order for all categories
    const updatedCategories = newCategories.map((cat, index) => ({
      ...cat,
      display_order: index + 1
    }));
    
    // Optimistic update
    setProductCustomizations({
      ...productCustomizations,
      categories: updatedCategories
    });
    
    // Save to backend
    try {
      for (const cat of updatedCategories) {
        await updateCustomizationCategory(cat.category_id, {
          product_id: cat.product_id,
          name: cat.name,
          display_order: cat.display_order,
          description: cat.description || ''
        });
      }
      toast.success('Category order updated');
    } catch (error) {
      toast.error('Failed to update category order');
      // Revert on error
      fetchProductCustomizations(selectedProduct.product_id);
    }
  };

  // Handle option drag end
  const handleOptionDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Only handle option drags (IDs prefixed with 'opt_')
    if (!active.id.startsWith('opt_')) return;
    
    // Find which category this option belongs to
    let categoryId = null;
    let category = null;
    
    for (const cat of productCustomizations.categories) {
      if (cat.options?.some(o => `opt_${o.option_id}` === active.id)) {
        categoryId = cat.category_id;
        category = cat;
        break;
      }
    }
    
    if (!category) return;
    
    const oldIndex = category.options.findIndex(o => `opt_${o.option_id}` === active.id);
    const newIndex = category.options.findIndex(o => `opt_${o.option_id}` === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newOptions = arrayMove(category.options, oldIndex, newIndex);
    
    // Update display_order for all options
    const updatedOptions = newOptions.map((opt, index) => ({
      ...opt,
      display_order: index + 1
    }));
    
    // Optimistic update
    const updatedCategories = productCustomizations.categories.map(cat => 
      cat.category_id === categoryId 
        ? { ...cat, options: updatedOptions }
        : cat
    );
    
    setProductCustomizations({
      ...productCustomizations,
      categories: updatedCategories
    });
    
    // Save to backend
    try {
      for (const opt of updatedOptions) {
        await updateCustomizationOption(opt.option_id, {
          ...opt,
          display_order: opt.display_order
        });
      }
      toast.success('Option order updated');
    } catch (error) {
      toast.error('Failed to update option order');
      // Revert on error
      fetchProductCustomizations(selectedProduct.product_id);
    }
  };

  // Combined drag handler
  const handleDragEnd = async (event) => {
    const { active } = event;
    
    if (active.id.startsWith('cat_')) {
      await handleCategoryDragEnd(event);
    } else if (active.id.startsWith('opt_')) {
      await handleOptionDragEnd(event);
    }
  };

  return (
    <div className="space-y-8 p-3" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Products & Customizations
          </h1>
          <p className="text-muted-foreground">Manage products and their customization options</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleSeedProducts}
            data-testid="seed-products-button"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Seed Defaults
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetProductForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
                data-testid="add-product-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                  {editingProduct ? 'Edit Product' : 'New Product'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-uppercase">Product Name *</Label>
                    <Input
                      data-testid="product-name-input"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      placeholder="e.g., Jacket"
                      className="h-11 bg-secondary/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-uppercase">Base Price (USD) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="product-price-input"
                      value={productForm.base_price}
                      onChange={(e) => setProductForm({ ...productForm, base_price: e.target.value })}
                      placeholder="0.00"
                      className="h-11 bg-secondary/50"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-uppercase">Product Prefix</Label>
                    <Input
                      data-testid="product-prefix-input"
                      value={productForm.product_prefix}
                      onChange={(e) => setProductForm({ ...productForm, product_prefix: e.target.value })}
                      placeholder="e.g., J"
                      className="h-11 bg-secondary/50"
                    />
                    {technicalPrefixPreview && (
                      <p className="text-[11px] text-muted-foreground" data-testid="product-prefix-key-preview">
                        Stored prefix key: <span className="font-mono text-foreground">{technicalPrefixPreview}</span>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="label-uppercase">Default Garment Type</Label>
                    <Input
                      data-testid="product-garment-type-input"
                      value={productForm.default_garment_type}
                      onChange={(e) => setProductForm({ ...productForm, default_garment_type: e.target.value })}
                      placeholder="e.g., jacket"
                      className="h-11 bg-secondary/50"
                    />
                    <p className="text-[11px] text-muted-foreground" data-testid="product-garment-key-preview">
                      Technical key (DB):{' '}
                      <span className="font-mono text-foreground">
                        {technicalGarmentKeyPreview || '—'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Share Customizations With</Label>
                  <Select
                    value={productForm.shared_customization_product_id}
                    onValueChange={(value) => setProductForm({ ...productForm, shared_customization_product_id: value })}
                  >
                    <SelectTrigger data-testid="product-share-select" className="h-11 bg-secondary/50">
                      <SelectValue placeholder="None (own customizations)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (own customizations)</SelectItem>
                      {products.filter(p => !p.shared_customization_product_id && p.product_id !== editingProduct?.product_id).map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">For test products sharing options with main products</p>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Description</Label>
                  <Textarea
                    data-testid="product-description-input"
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    placeholder="Product description..."
                    className="bg-secondary/50 min-h-[60px]"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-product-button"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {editingProduct ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List */}
        <div className="lg:col-span-1">
          <Card className="border-white/10 bg-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Products</CardTitle>
                <Badge variant="outline">{products.length}</Badge>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="product-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9 bg-secondary/50"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Tag className="w-10 h-10 mx-auto mb-3 opacity-50" strokeWidth={1} />
                  <p>No products found</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.product_id}
                      className={`p-4 cursor-pointer transition-colors hover:bg-white/5 ${
                        selectedProduct?.product_id === product.product_id ? 'bg-primary/10 border-l-2 border-primary' : ''
                      }`}
                      onClick={() => setSelectedProduct(product)}
                      data-testid={`product-item-${product.product_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{product.name}</span>
                            {product.shared_customization_product_id && (
                              <Link2 className="w-3 h-3 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-primary font-medium">
                              {formatCurrency(product.base_price)}
                            </span>
                            {product.product_prefix && (
                              <Badge variant="outline" className="text-xs">
                                {product.product_prefix}
                              </Badge>
                            )}
                          </div>
                          {product.default_garment_type && (
                            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`product-technical-key-${product.product_id}`}>
                              key: <span className="font-mono">{normalizeGarmentTypeKey(product.default_garment_type)}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); handleEditProduct(product); }}
                            data-testid={`edit-product-${product.product_id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.product_id); }}
                            data-testid={`delete-product-${product.product_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Customizations Panel */}
        <div className="lg:col-span-2">
          {selectedProduct ? (
            <Card className="border-white/10 bg-card">
              <CardHeader className="border-b border-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      {selectedProduct.name} Customizations
                    </CardTitle>
                    {selectedProduct.shared_customization_product_id && (
                      <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        Shares customizations with {products.find(p => p.product_id === selectedProduct.shared_customization_product_id)?.name}
                      </p>
                    )}
                  </div>
                  {!selectedProduct.shared_customization_product_id && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCopyDialogOpen(true)}
                        data-testid="copy-customizations-button"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy From
                      </Button>
                      <Button
                        size="sm"
                        onClick={openAddCategory}
                        data-testid="add-category-button"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {!productCustomizations ? (
                  <div className="p-8 text-center">
                    <Skeleton className="h-32 w-full" />
                  </div>
                ) : productCustomizations.categories.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">
                    <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                    <p className="text-lg mb-2">No customizations defined</p>
                    <p className="text-sm">Add categories and options to enable product customization</p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={[
                        ...productCustomizations.categories.map(c => `cat_${c.category_id}`),
                        ...productCustomizations.categories.flatMap(c => 
                          c.options?.map(o => `opt_${o.option_id}`) || []
                        )
                      ]}
                      strategy={verticalListSortingStrategy}
                    >
                      <Accordion type="multiple" className="w-full">
                        {productCustomizations.categories.map((category, idx) => (
                          <SortableCategory
                            key={category.category_id}
                            category={category}
                            handleEditCategory={handleEditCategory}
                            handleDeleteCategory={handleDeleteCategory}
                            sharedCustomization={!!selectedProduct.shared_customization_product_id}
                          >
                            {/* Category actions */}
                            {!selectedProduct.shared_customization_product_id && (
                              <div className="flex gap-2 mb-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditCategory(category)}
                                >
                                  <Edit2 className="w-3 h-3 mr-1" />
                                  Edit Category
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteCategory(category.category_id)}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete Category
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const catId = category.category_id;
                                    setSelectedCategoryId(catId);
                                    setEditingOption(null);
                                    setOptionForm({
                                      category_id: catId,
                                      name: '',
                                      option_type: 'text',
                                      options: '',
                                      price_impact: '0',
                                      default_value: '',
                                      display_order: '1.0',
                                      is_required: false,
                                      show_on_invoice: true,
                                      is_fabric_selector: false,
                                      fabric_selector_type: '__none__',
                                      conditional_on_option_id: '__none__',
                                      conditional_operator: 'equals',
                                      conditional_on_values: '',
                                      placeholder: '',
                                    });
                                    setOptionDialogOpen(true);
                                  }}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Option
                                </Button>
                              </div>
                            )}

                            {/* Options list */}
                            {category.options && category.options.length > 0 ? (
                              <div className="space-y-2">
                                {category.options.map((option) => (
                                  <SortableOption
                                    key={option.option_id}
                                    option={option}
                                    handleEditOption={handleEditOption}
                                    handleDeleteOption={handleDeleteOption}
                                    sharedCustomization={!!selectedProduct.shared_customization_product_id}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground py-2">No options defined</p>
                            )}
                          </SortableCategory>
                        ))}
                      </Accordion>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-white/10 bg-card">
              <CardContent className="p-12 text-center text-muted-foreground">
                <Settings className="w-16 h-16 mx-auto mb-4 opacity-30" strokeWidth={1} />
                <p className="text-lg">Select a product to manage customizations</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
        setCategoryDialogOpen(open);
        if (!open) resetCategoryForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingCategory ? 'Edit Category' : 'New Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-uppercase">Category Name *</Label>
              <Input
                data-testid="category-name-input"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Collar Details"
                className="h-11 bg-secondary/50"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-uppercase">Display Order</Label>
              <Input
                type="number"
                step="0.1"
                data-testid="category-order-input"
                value={categoryForm.display_order}
                onChange={(e) => setCategoryForm({ ...categoryForm, display_order: e.target.value })}
                className="h-11 bg-secondary/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-uppercase">Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Category description..."
                className="bg-secondary/50"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Option Dialog */}
      <Dialog open={optionDialogOpen} onOpenChange={(open) => {
        setOptionDialogOpen(open);
        if (!open) resetOptionForm();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingOption ? 'Edit Option' : 'New Option'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOptionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Option Name *</Label>
                <Input
                  data-testid="option-name-input"
                  value={optionForm.name}
                  onChange={(e) => setOptionForm({ ...optionForm, name: e.target.value })}
                  placeholder="e.g., Collar Type"
                  className="h-11 bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Option Type *</Label>
                <Select
                  value={optionForm.option_type}
                  onValueChange={(value) => setOptionForm({ ...optionForm, option_type: value })}
                >
                  <SelectTrigger className="h-11 bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {optionTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(optionForm.option_type === 'select' || optionForm.option_type === 'radio') && (
              <div className="space-y-2">
                <Label className="label-uppercase">Options (comma-separated) *</Label>
                <Textarea
                  data-testid="option-choices-input"
                  value={optionForm.options}
                  onChange={(e) => setOptionForm({ ...optionForm, options: e.target.value })}
                  placeholder="Option 1, Option 2, Option 3"
                  className="bg-secondary/50"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Price Impact</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={optionForm.price_impact}
                  onChange={(e) => setOptionForm({ ...optionForm, price_impact: e.target.value })}
                  className="h-11 bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Default Value</Label>
                <Input
                  value={optionForm.default_value}
                  onChange={(e) => setOptionForm({ ...optionForm, default_value: e.target.value })}
                  placeholder="Default..."
                  className="h-11 bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Display Order</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={optionForm.display_order}
                  onChange={(e) => setOptionForm({ ...optionForm, display_order: e.target.value })}
                  className="h-11 bg-secondary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-uppercase">Placeholder Text</Label>
              <Input
                value={optionForm.placeholder}
                onChange={(e) => setOptionForm({ ...optionForm, placeholder: e.target.value })}
                placeholder="Input placeholder..."
                className="h-11 bg-secondary/50"
              />
            </div>

            <div className="flex items-center gap-6 py-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_required"
                  checked={optionForm.is_required}
                  onCheckedChange={(checked) => setOptionForm({ ...optionForm, is_required: checked })}
                />
                <Label htmlFor="is_required" className="text-sm cursor-pointer">Required field</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show_on_invoice"
                  checked={optionForm.show_on_invoice !== false}
                  onCheckedChange={(checked) => setOptionForm({ ...optionForm, show_on_invoice: checked })}
                  data-testid="show-on-invoice-checkbox"
                />
                <Label htmlFor="show_on_invoice" className="text-sm cursor-pointer">Show on customer invoice</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_fabric_selector"
                  checked={optionForm.is_fabric_selector}
                  onCheckedChange={(checked) => setOptionForm({ ...optionForm, is_fabric_selector: checked })}
                />
                <Label htmlFor="is_fabric_selector" className="text-sm cursor-pointer">Shared Order Reference (Fabric/Button)</Label>
              </div>
            </div>

            {optionForm.is_fabric_selector && (
              <div className="space-y-2">
                <Label className="label-uppercase">Reference Type</Label>
                <Select
                  value={optionForm.fabric_selector_type}
                  onValueChange={(value) => setOptionForm({ ...optionForm, fabric_selector_type: value })}
                >
                  <SelectTrigger className="h-11 bg-secondary/50">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select type...</SelectItem>
                    {fabricSelectorTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="border-t border-white/10 pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Conditional Logic</p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="label-uppercase">Show when this option...</Label>
                  <Select
                    value={optionForm.conditional_on_option_id}
                    onValueChange={(value) => setOptionForm({ ...optionForm, conditional_on_option_id: value })}
                  >
                    <SelectTrigger className="h-11 bg-secondary/50">
                      <SelectValue placeholder="Always show (no condition)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Always show (no condition)</SelectItem>
                      {getAllOptions()
                        .filter(o => o.option_id !== editingOption?.option_id)
                        .map((opt) => (
                          <SelectItem key={opt.option_id} value={opt.option_id}>
                            {opt.categoryName} → {opt.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {optionForm.conditional_on_option_id && optionForm.conditional_on_option_id !== '__none__' && (
                  <>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Operator</Label>
                      <Select
                        value={optionForm.conditional_operator}
                        onValueChange={(value) => setOptionForm({ ...optionForm, conditional_operator: value })}
                      >
                        <SelectTrigger className="h-11 bg-secondary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals (any of these values)</SelectItem>
                          <SelectItem value="not_equals">Not Equals (none of these values)</SelectItem>
                          <SelectItem value="is_empty">Is Empty (has no value)</SelectItem>
                          <SelectItem value="is_not_empty">Is Not Empty (has any value)</SelectItem>
                          <SelectItem value="contains">Contains (includes substring)</SelectItem>
                          <SelectItem value="not_contains">Not Contains (excludes substring)</SelectItem>
                          <SelectItem value="greater_than">Greater Than (numeric)</SelectItem>
                          <SelectItem value="less_than">Less Than (numeric)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {!['is_empty', 'is_not_empty'].includes(optionForm.conditional_operator) && (
                      <div className="space-y-2">
                        <Label className="label-uppercase">
                          {optionForm.conditional_operator === 'equals' || optionForm.conditional_operator === 'not_equals' 
                            ? 'Values (comma-separated)'
                            : optionForm.conditional_operator === 'contains' || optionForm.conditional_operator === 'not_contains'
                            ? 'Substring to match'
                            : 'Numeric value'}
                        </Label>
                        <Input
                          value={optionForm.conditional_on_values}
                          onChange={(e) => setOptionForm({ ...optionForm, conditional_on_values: e.target.value })}
                          placeholder={
                            optionForm.conditional_operator === 'equals' || optionForm.conditional_operator === 'not_equals'
                              ? 'Value 1, Value 2'
                              : optionForm.conditional_operator === 'contains' || optionForm.conditional_operator === 'not_contains'
                              ? 'e.g., silk'
                              : 'e.g., 7'
                          }
                          className="h-11 bg-secondary/50"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setOptionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {editingOption ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Copy Customizations Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={(open) => {
        setCopyDialogOpen(open);
        if (!open) {
          setCopySourceProductId('__none__');
          setCopyReplaceExisting(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Copy Customizations
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Copy all customization categories and options from another product to <strong>{selectedProduct?.name}</strong>.
            </p>
            
            <div className="space-y-2">
              <Label className="label-uppercase">Source Product *</Label>
              <Select
                value={copySourceProductId}
                onValueChange={setCopySourceProductId}
              >
                <SelectTrigger className="h-11 bg-secondary/50" data-testid="copy-source-select">
                  <SelectValue placeholder="Select a product to copy from" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select a product...</SelectItem>
                  {products
                    .filter(p => 
                      p.product_id !== selectedProduct?.product_id && 
                      !p.shared_customization_product_id
                    )
                    .map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start space-x-3 rounded-md bg-secondary/30 p-4">
              <Checkbox
                id="replace-existing"
                checked={copyReplaceExisting}
                onCheckedChange={setCopyReplaceExisting}
              />
              <div className="space-y-1">
                <label
                  htmlFor="replace-existing"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Replace existing customizations
                </label>
                <p className="text-xs text-muted-foreground">
                  If checked, all existing categories and options on {selectedProduct?.name} will be deleted before copying.
                  Otherwise, copied items will be added alongside existing ones.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCopyCustomizations}
                disabled={copySourceProductId === '__none__' || copying}
                data-testid="confirm-copy-button"
              >
                {copying ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                    Copying...
                  </span>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Customizations
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
